import { createMemo, Index, Match, Switch } from "solid-js";
import type { ExtendableInputProps } from "../inputs/Input";
import Input from "../inputs/Input";
import {
  createListCollection,
  SelectContent,
  SelectControl,
  SelectItem,
  SelectPositioner,
  SelectRoot,
  SelectTrigger,
} from "@ark-ui/solid";
import { isEmpty, isEqual, isNil } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import Portal from "../../utils/Portal";
import TextListItem from "./listItems/TextListItem";
import MenuEmptyState from "./MenuEmptyState";
import MultiSelectTrigger from "./triggers/MultiSelectTrigger";
import type { SelectOption } from "../../types/Menu";
import { narrow } from "../../utils/solidUtils";
import IconListItem from "./listItems/IconListItem";
import ImageListItem from "./listItems/ImageListItem";
import DynamicValueListItem from "./listItems/DynamicValueListItem";

type MultiSelectProps<T> = {
  id: string;
  ariaLabel: string;
  options: SelectOption<T>[];
  value?: SelectOption<T>["value"][] | null;
  onSelect: (value: SelectOption<T>["value"][]) => void;
  onClear?: () => void;
  sameWidth: boolean;
  hasError: boolean;
};

const MultiSelect = <T,>(props: MultiSelectProps<T>) => {
  let triggerRef!: HTMLButtonElement | null;
  const { t } = useTranslation();
  const selectedOptions = createMemo(() => {
    if (isEmpty(props.value) || isNil(props.value)) {
      return [];
    }
    const optionMap = new Map(props.options.map((option) => [JSON.stringify(option.value), option]));
    return props.value.map((val) => optionMap.get(JSON.stringify(val))).filter((option) => !isNil(option));
  });

  const handleClear = (option: SelectOption<T>) => {
    props.onSelect(
      selectedOptions()
        .filter((o) => !isEqual(o.value, option.value))
        .map((o) => o.value),
    );

    triggerRef?.focus();
  };

  return (
    <SelectRoot
      collection={createListCollection({
        items: props.options ?? [],
        itemToValue: (item) => JSON.stringify(item.value),
        itemToString: (item) => item.label,
      })}
      value={selectedOptions().map((option) => JSON.stringify(option.value))}
      onValueChange={(option) => props.onSelect(option.items.map((item) => item.value))}
      positioning={{ placement: "bottom-start", gutter: 4, sameWidth: props.sameWidth }}
      multiple
    >
      <SelectControl>
        <SelectTrigger
          asChild={(triggerProps) => (
            <MultiSelectTrigger
              ref={triggerRef!}
              value={selectedOptions().map((option) => ({
                variant: "text" as const,
                value: option.value,
                hidden: option.hidden,
                disabled: option.disabled,
                label: option.label,
              }))}
              ariaLabel={props.ariaLabel}
              hasError={props.hasError}
              onClear={handleClear}
              arkProps={triggerProps()}
            />
          )}
        />
      </SelectControl>

      <Portal>
        <SelectPositioner>
          <SelectContent
            aria-label={props.ariaLabel}
            class="z-50 max-h-86 overflow-y-auto rounded-sm bg-surface-default p-1 shadow-elevation-light-3 outline-none"
            classList={{ "max-w-170 min-w-66": !props.sameWidth }}
          >
            <Index each={props.options} fallback={<MenuEmptyState text={t(($) => $.SELECT_EMPTY_STATE_TITLE)} />}>
              {(option) => (
                <SelectItem
                  item={option()}
                  asChild={(arkProps) => (
                    <Switch>
                      <Match when={narrow(option, (o) => o.variant === "image")}>
                        {(o) => <ImageListItem htmlProps={arkProps()} {...o()} />}
                      </Match>
                      <Match when={narrow(option, (o) => o.variant === "icon")}>
                        {(o) => <IconListItem htmlProps={arkProps()} {...o()} />}
                      </Match>
                      <Match when={narrow(option, (o) => o.variant === "text")}>
                        {(o) => <TextListItem htmlProps={arkProps()} {...o()} />}
                      </Match>
                      <Match when={narrow(option, (o) => o.variant === "dynamicValue")}>
                        {(o) => <DynamicValueListItem htmlProps={arkProps()} {...o()} />}
                      </Match>
                    </Switch>
                  )}
                />
              )}
            </Index>
          </SelectContent>
        </SelectPositioner>
      </Portal>
    </SelectRoot>
  );
};

export default MultiSelect;

export type MultiSelectInputProps<T> = Omit<MultiSelectProps<T>, "id" | "ariaLabel" | "hasError"> &
  ExtendableInputProps;
export const MultiSelectInput = <T,>(props: MultiSelectInputProps<T>) => (
  <Input required={props.required} errorMessage={props.errorMessage} info={props.info} label={props.label}>
    {(id) => <MultiSelect<T> id={id} ariaLabel={props.label} {...props} hasError={!isNil(props.errorMessage)} />}
  </Input>
);
